// =============================================
// Leaderboard API Service for MediCureOn
// Enterprise-grade leaderboard management with Azure Functions integration
// Handles global rankings, community leaders, and user positioning
// =============================================

import { apiConfig } from '../config/apiConfig';

class LeaderboardApiService {
  constructor() {
    this.baseUrl = apiConfig.backendUrl;
    this.functionKey = apiConfig.functionKeys.leaderboardManager;
    this.endpoint = '/api/leaderboard-manager';
  }

  // Helper method to get headers with authentication and region info
  getHeaders(userCountry = 'United States', authToken = null, subscriptionTier = null) {
    const headers = {
      'Content-Type': 'application/json',
      'X-User-Country': userCountry
    };
    
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    if (subscriptionTier) {
      headers['X-User-Subscription-Tier'] = subscriptionTier;
    }
    
    return headers;
  }

  // Helper method to build URL with function key and parameters
  buildUrl(queryParams = {}, userCountry = 'United States') {
    const url = new URL(`${this.baseUrl}${this.endpoint}`);
    url.searchParams.append('code', this.functionKey);
    url.searchParams.append('country', userCountry);
    
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        url.searchParams.append(key, value.toString());
      }
    });
    
    return url.toString();
  }

  // Get Global Top 25 Leaderboard
  async getGlobalTop25(authToken, userCountry = 'United States', subscriptionTier = 'free') {
    try {
      const url = this.buildUrl({
        action: 'get_leaderboard',
        type: 'points',
        timeframe: 'all_time',
        limit: 25,
        offset: 0
      }, userCountry);

      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(userCountry, authToken, subscriptionTier),
        body: JSON.stringify({
          action: 'get_leaderboard',
          type: 'points',
          timeframe: 'all_time',
          limit: 25,
          offset: 0
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        return {
          success: true,
          leaderboard: data.data.leaderboard || [],
          totalUsers: data.data.totalEntries || 0,
          lastUpdated: data.data.lastUpdated || new Date().toISOString()
        };
      } else {
        throw new Error(data.details || 'Failed to fetch global leaderboard');
      }
    } catch (error) {
      console.error('Error fetching global top 25:', error);
      return {
        success: false,
        error: error.message,
        leaderboard: [],
        totalUsers: 0
      };
    }
  }

  // Get User's Current Rank and Position
  async getUserRank(userId, authToken, userCountry = 'United States', subscriptionTier = 'free') {
    try {
      const url = this.buildUrl({
        action: 'get_user_rank',
        userId: userId,
        type: 'points',
        timeframe: 'all_time'
      }, userCountry);

      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(userCountry, authToken, subscriptionTier),
        body: JSON.stringify({
          action: 'get_user_rank',
          userId: userId,
          type: 'points', 
          timeframe: 'all_time'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        return {
          success: true,
          rank: data.rank,
          totalPoints: data.totalPoints || 0,
          currentPoints: data.currentPoints || 0,
          redeemedPoints: data.redeemedPoints || 0,
          title: data.title || null,
          level: data.level || 1,
          country: data.country || userCountry,
          username: data.username || 'Health Champion'
        };
      } else {
        throw new Error(data.message || 'Failed to fetch user rank');
      }
    } catch (error) {
      console.error('Error fetching user rank:', error);
      return {
        success: false,
        error: error.message,
        rank: null
      };
    }
  }

  // Get Community Leaders (users who can mentor others)
  async getCommunityLeaders(authToken, userCountry = 'United States', subscriptionTier = 'free') {
    try {
      const url = this.buildUrl({
        action: 'getCommunityLeaders',
        minPoints: 10000 // Users with 10K+ total points can mentor
      }, userCountry);

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(userCountry, authToken, subscriptionTier)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        return {
          success: true,
          leaders: data.leaders || [],
          totalLeaders: data.totalLeaders || 0
        };
      } else {
        throw new Error(data.message || 'Failed to fetch community leaders');
      }
    } catch (error) {
      console.error('Error fetching community leaders:', error);
      return {
        success: false,
        error: error.message,
        leaders: []
      };
    }
  }

  // Get Country Rankings
  async getCountryRankings(country, authToken, userCountry = 'United States', subscriptionTier = 'free') {
    try {
      const url = this.buildUrl({
        action: 'getCountryRankings',
        targetCountry: country,
        count: 50
      }, userCountry);

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(userCountry, authToken, subscriptionTier)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        return {
          success: true,
          rankings: data.rankings || [],
          country: data.country || country,
          totalUsers: data.totalUsers || 0
        };
      } else {
        throw new Error(data.message || 'Failed to fetch country rankings');
      }
    } catch (error) {
      console.error('Error fetching country rankings:', error);
      return {
        success: false,
        error: error.message,
        rankings: []
      };
    }
  }

  // Get Nearby Rankings (users ranked around current user)
  async getNearbyRankings(userId, authToken, userCountry = 'United States', subscriptionTier = 'free', range = 10) {
    try {
      const url = this.buildUrl({
        action: 'getNearby',
        userId: userId,
        range: range
      }, userCountry);

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(userCountry, authToken, subscriptionTier)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        return {
          success: true,
          rankings: data.rankings || [],
          userRank: data.userRank || null
        };
      } else {
        throw new Error(data.message || 'Failed to fetch nearby rankings');
      }
    } catch (error) {
      console.error('Error fetching nearby rankings:', error);
      return {
        success: false,
        error: error.message,
        rankings: []
      };
    }
  }

  // Update User Points in Leaderboard (called when user earns points)
  async updateUserPoints(userId, pointsEarned, authToken, userCountry = 'United States', subscriptionTier = 'free') {
    try {
      const url = this.buildUrl({}, userCountry);

      const requestBody = {
        action: 'update',
        userId: userId,
        pointsEarned: pointsEarned,
        timestamp: new Date().toISOString()
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(userCountry, authToken, subscriptionTier),
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        return {
          success: true,
          newRank: data.newRank || null,
          totalPoints: data.totalPoints || 0,
          message: data.message || 'Leaderboard updated successfully'
        };
      } else {
        throw new Error(data.message || 'Failed to update leaderboard');
      }
    } catch (error) {
      console.error('Error updating leaderboard:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Refresh Leaderboard Cache (admin function)
  async refreshCache(authToken, userCountry = 'United States') {
    try {
      const url = this.buildUrl({
        action: 'refreshCache'
      }, userCountry);

      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(userCountry, authToken)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      return {
        success: data.success || false,
        message: data.message || 'Cache refresh completed'
      };
    } catch (error) {
      console.error('Error refreshing leaderboard cache:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get Global Stats Summary
  async getGlobalStats(authToken, userCountry = 'United States') {
    try {
      const url = this.buildUrl({
        action: 'getGlobalStats'
      }, userCountry);

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(userCountry, authToken)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        return {
          success: true,
          totalUsers: data.totalUsers || 0,
          totalPointsEarned: data.totalPointsEarned || 0,
          averagePoints: data.averagePoints || 0,
          topCountries: data.topCountries || [],
          lastUpdated: data.lastUpdated || new Date().toISOString()
        };
      } else {
        throw new Error(data.message || 'Failed to fetch global stats');
      }
    } catch (error) {
      console.error('Error fetching global stats:', error);
      return {
        success: false,
        error: error.message,
        totalUsers: 0,
        totalPointsEarned: 0
      };
    }
  }
}

// Export singleton instance
const leaderboardApiService = new LeaderboardApiService();
export default leaderboardApiService;